import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PushSendTimePoint } from '../../lib/api/analytics';

/**
 * Hour-of-day bar chart of breaking-news push send-counts (left axis) with a
 * line overlay of CTR (right axis). Africa/Lagos hours.
 *
 * Why this view: closes the loop on the breaking-news system. You can see
 * at a glance which send-time windows produced the strongest tap-through
 * — and conversely, which hours have a lot of sends but weak engagement
 * (signal that they're badly timed). Use it to bias the
 * `auto-breaking-news` scheduler's send-time choices toward proven peaks.
 */
interface PushSendTimeCtrProps {
  points: PushSendTimePoint[];
}

export function PushSendTimeCtr({ points }: PushSendTimeCtrProps): JSX.Element {
  // Densify to all 24 hours so empty hours show as zero bars.
  const data = useMemo(() => {
    const byHour = new Map(points.map((p) => [p.hour, p]));
    return Array.from({ length: 24 }, (_, h) => {
      const p = byHour.get(h);
      return {
        hour: h,
        label: `${String(h).padStart(2, '0')}:00`,
        sent: p?.sent ?? 0,
        delivered: p?.delivered ?? 0,
        taps: p?.taps ?? 0,
        ctrPct: p?.ctr != null ? p.ctr * 100 : null,
      };
    });
  }, [points]);

  const totals = useMemo(() => {
    let sent = 0;
    let delivered = 0;
    let taps = 0;
    for (const p of points) {
      sent += p.sent;
      delivered += p.delivered;
      taps += p.taps;
    }
    const ctr = delivered > 0 ? (taps / delivered) * 100 : null;
    return { sent, delivered, taps, ctr };
  }, [points]);

  // Identify the hour with the best CTR (require ≥ a small denominator so
  // a 1-send/1-tap accident doesn't claim "best window").
  const bestHour = useMemo(() => {
    let best: typeof data[number] | null = null;
    for (const d of data) {
      if (d.delivered < 50) continue;
      if (d.ctrPct == null) continue;
      if (!best || d.ctrPct > (best.ctrPct ?? -1)) best = d;
    }
    return best;
  }, [data]);

  return (
    <div>
      <div className="mb-3 grid grid-cols-4 gap-3 text-xs text-ink-muted">
        <Stat label="Sends" value={totals.sent.toLocaleString()} />
        <Stat label="Delivered ≈" value={totals.delivered.toLocaleString()} />
        <Stat label="Taps" value={totals.taps.toLocaleString()} />
        <Stat
          label="Avg CTR"
          value={totals.ctr != null ? `${totals.ctr.toFixed(2)}%` : '—'}
        />
      </div>

      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              dataKey="label"
              stroke="#71717a"
              tick={{ fontSize: 10 }}
              interval={2}
            />
            <YAxis
              yAxisId="left"
              stroke="#71717a"
              tick={{ fontSize: 10 }}
              label={{ value: 'sends', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 10 }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#71717a"
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => `${v}%`}
              label={{ value: 'CTR', angle: 90, position: 'insideRight', fill: '#71717a', fontSize: 10 }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(220, 38, 38, 0.04)' }}
              contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e4e4e7' }}
              formatter={(value: number, name: string) => {
                if (name === 'CTR') return [value != null ? `${value.toFixed(2)}%` : '—', name];
                return [value.toLocaleString(), name];
              }}
            />
            <Bar yAxisId="left" dataKey="sent" name="Sends" fill="#dc2626" opacity={0.85} radius={[3, 3, 0, 0]} />
            <Line
              yAxisId="right"
              dataKey="ctrPct"
              name="CTR"
              stroke="#0ea5e9"
              strokeWidth={2}
              dot={{ r: 2 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {bestHour ? (
        <p className="mt-3 text-xs text-ink-muted">
          <strong className="text-ink">Best send-window:</strong> {bestHour.label}–
          {String((bestHour.hour + 1) % 24).padStart(2, '0')}:00 Lagos —{' '}
          <span className="font-mono">{bestHour.ctrPct?.toFixed(2)}%</span> CTR
          across <span className="font-mono">{bestHour.delivered.toLocaleString()}</span>{' '}
          delivered.
        </p>
      ) : (
        <p className="mt-3 text-xs text-ink-subtle">
          Not enough delivered pushes per hour to call out a best window yet.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <span className="block font-mono uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span className="text-base font-semibold text-ink">{value}</span>
    </div>
  );
}
